import { createSlice } from '@reduxjs/toolkit';

import { addStateFetch } from '@webapp/account/src/resources/store-utils';

import {
    initialLogicSubCondition,
    type LogicCondition,
    LogicSubConditonType,
    type LogicVariant,
} from '@webapp/common/entities/logic';
import { updateItemInArray } from '@webapp/common/lib/utils';
import { LogicType, QuestionType } from '@webapp/common/resources/survey';

import type { Page } from '../common';

import {
    addExtendedLogic,
    addLogicCondition,
    addLogicSubCondition,
    changeExtendedLogicConditionParam,
    changeLogicConditionParam,
    changeLogicQuestionId,
    changeLogicSubCondition,
    changeLogicVariant,
    loadExtendedLogic,
    loadLogicData,
    loadSurveyLogic,
    removeExtendedLogic,
    removeLogicCondition,
    removeLogicSubCondition,
    resetLogicVariants,
} from './async';
import { createCondition, createNpsCondition, createStarCondition, updateCondition } from './lib';
import type { LogicState } from './logic';
import { initialState } from './logic';
import type { LogicPageQuestion } from './question';

export const logicSlice = createSlice({
    name: 'logic',
    initialState,
    reducers: {},
    extraReducers: (builder) =>
        addStateFetch(
            builder
                .addCase(addLogicCondition.fulfilled, ({ pages }, { payload: { id, pageId, params, questionId } }) => {
                    if (pageId && questionId) {
                        updateItemInArray(pages, pageId, (p: Page) => {
                            updateItemInArray(p.questions, questionId, (q: LogicPageQuestion) => {
                                q.expand = true;

                                switch (q.type) {
                                    case QuestionType.NPS:
                                        q.conditions.push(createNpsCondition(id, params, q.params));
                                        break;
                                    case QuestionType.STAR:
                                        q.conditions.push(
                                            createStarCondition(id, Number(q.params.amount), params, q.params),
                                        );
                                        break;
                                    default:
                                        q.conditions.push(createCondition(id, q, params));
                                        break;
                                }
                            });
                        });
                    }
                })
                .addCase(
                    removeLogicCondition.fulfilled,
                    ({ pages, logics }: LogicState, { payload: { conditionId, pageId, questionId } }): void => {
                        if (pageId && questionId) {
                            updateItemInArray(pages, pageId, (p: Page) => {
                                updateItemInArray(p.questions, questionId, (q: LogicPageQuestion) => {
                                    q.conditions = q.conditions.filter((c) => c.id !== conditionId);
                                });
                            });
                        } else {
                            const idx = logics.findIndex((l) => l.id === conditionId);
                            if (idx !== -1) {
                                logics.splice(idx, 1);
                            }
                        }
                    },
                )
                .addCase(
                    changeLogicVariant.pending,
                    (
                        { pages }: LogicState,
                        {
                            meta: {
                                arg: { checked, conditionId, pageId, questionId, variantId },
                            },
                        },
                    ): void => {
                        updateCondition(pages, pageId, questionId, conditionId, (c) => {
                            updateItemInArray(c.variants, variantId, (v: LogicVariant) => {
                                v.checked = checked;
                            });
                        });
                    },
                )
                .addCase(
                    resetLogicVariants.pending,
                    (
                        { pages }: LogicState,
                        {
                            meta: {
                                arg: { newVariants, conditionId, pageId, questionId },
                            },
                        },
                    ): void => {
                        updateCondition(pages, pageId, questionId, conditionId, (c) => {
                            c.variants = newVariants;
                        });
                    },
                )
                .addCase(
                    changeLogicQuestionId.pending,
                    (
                        { pages }: LogicState,
                        {
                            meta: {
                                arg: { oldQuestionId, conditionId, questionId },
                            },
                        },
                    ) => {
                        // id вопросов глобально уникальны, поэтому pageId не нужен:
                        // ищем старый и новый вопрос по всем страницам — так работает
                        // и перенос между страницами, и вызов со страницы квот, где
                        // pageId не передаётся.
                        let condition: LogicCondition | null = null;
                        pages.forEach((p: Page) => {
                            updateItemInArray(p.questions, oldQuestionId, (q: LogicPageQuestion) => {
                                const cond = q.conditions.find((c) => c.id === conditionId);
                                if (cond) {
                                    condition = cond;
                                }
                                q.conditions = q.conditions.filter((c) => c.id !== conditionId);
                            });
                        });
                        if (!condition) return;
                        pages.forEach((p: Page) => {
                            updateItemInArray(p.questions, questionId, (q: LogicPageQuestion) => {
                                q.conditions.push(condition);
                            });
                        });
                    },
                )
                .addCase(
                    changeLogicConditionParam.fulfilled,
                    ({ pages, logics }: LogicState, { payload: { conditionId, pageId, params, questionId } }): void => {
                        // квота на весь опрос живёт в logics, а не в pages
                        if (!pageId || !questionId) {
                            const logic = logics.find((l) => l.id === conditionId);
                            if (logic) {
                                logic.fetch = false;
                                Object.assign(logic, params);
                            }
                            return;
                        }
                        updateCondition(pages, pageId, questionId, conditionId, (c: LogicCondition) => {
                            c.fetch = false;
                            Object.assign(c, params);
                            if (
                                params['respondent'] === LogicType.SKIP ||
                                params['respondent'] === LogicType.ALWAYS ||
                                params['respondent'] === LogicType.SELECT_COUNT ||
                                params['respondent'] === LogicType.CANT_RATE
                            ) {
                                c.variants.forEach((v: LogicVariant) => {
                                    v.checked = false;
                                });
                            }
                        });
                    },
                )
                .addCase(loadLogicData.pending, (s) => {
                    s.fetchPages = true;
                })
                .addCase(loadLogicData.rejected, (s) => {
                    s.fetchPages = false;
                })
                .addCase(loadLogicData.fulfilled, (s: LogicState, { payload: { pages, questions } }) => {
                    s.fetchPages = false;
                    s.pages = pages;
                    if (!s.fetchExtendedLogics) {
                        s.extednedLogics = s.extednedLogics.map((logic) => ({
                            ...logic,
                            completeText:
                                typeof logic.completeText !== 'string'
                                    ? logic.completeText
                                    : logic.completeText.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                          const q = questions.find((q: { id: number }) => q.id === parseInt(id, 10));
                                          if (!q) return '';
                                          return `{Q${q.order}}`;
                                      }),
                            disqualText:
                                typeof logic.disqualText !== 'string'
                                    ? logic.disqualText
                                    : logic.disqualText.replace(/\{Q\-([0-9]+)\}/gi, (_match, id) => {
                                          const q = questions.find((q: { id: number }) => q.id === parseInt(id, 10));
                                          if (!q) return '';
                                          return `{Q${q.order}}`;
                                      }),
                        }));
                    }

                    for (const q of questions) {
                        updateItemInArray(s.pages, q.pageId, (p: Page) => {
                            p.questions.push(q);
                        });
                    }
                })
                .addCase(loadSurveyLogic.fulfilled, (s, { payload: { logics } }) => {
                    s.logics = logics;
                    s.fetchLogics = false;
                })
                .addCase(loadSurveyLogic.pending, (s) => {
                    s.fetchLogics = true;
                })
                .addCase(loadExtendedLogic.pending, (s) => {
                    s.fetchExtendedLogics = true;
                })
                .addCase(loadExtendedLogic.rejected, (s) => {
                    s.fetchExtendedLogics = false;
                })
                .addCase(loadExtendedLogic.fulfilled, (s, { payload: { logics } }) => {
                    s.extednedLogics = logics;
                    s.fetchExtendedLogics = false;
                })
                .addCase(addExtendedLogic.fulfilled, (s, { payload: { id, params, message, subcond } }) => {
                    const createdLogic: LogicCondition = {
                        ...params,
                        id,
                        conditions: subcond ? [subcond] : [],
                        variants: [],
                        message,
                    };
                    s.extednedLogics.push(createdLogic);
                    s.lastAddedExtendedConditionId = createdLogic.id;
                })
                .addCase(changeExtendedLogicConditionParam.fulfilled, (s, { payload: { conditionId, params } }) => {
                    const logic = s.extednedLogics.find((l) => l.id === conditionId);
                    Object.keys(params).forEach((key) => {
                        logic[key] = params[key];
                    });
                })
                .addCase(removeExtendedLogic.fulfilled, (s, { payload: { conditionId } }) => {
                    s.extednedLogics = s.extednedLogics.filter((l) => l.id !== conditionId);
                })
                .addCase(addLogicSubCondition.fulfilled, (s, { payload: { logicId, params, id } }) => {
                    const logic = s.extednedLogics.find((l) => l.id === logicId);

                    if (logic) {
                        logic.conditions.push({
                            ...initialLogicSubCondition,
                            ...params,
                            type: params.type ?? LogicSubConditonType.QUESTION,
                            id,
                        });
                    }
                })
                .addCase(changeLogicSubCondition.fulfilled, (s, { payload: { logicId, params } }) => {
                    const logic = s.extednedLogics.find((l) => l.conditions.find((c) => c.id === logicId));
                    const subCondition = logic.conditions.find((sc) => sc.id === logicId);
                    if (subCondition) {
                        Object.keys(params).forEach((key) => {
                            subCondition[key] = params[key];
                        });
                    }
                })
                .addCase(removeLogicSubCondition.fulfilled, (s, { payload: { logicId, id } }) => {
                    const logic = s.extednedLogics.find((l) => l.id === logicId);
                    if (logic) {
                        logic.conditions = logic.conditions.filter((c) => c.id !== id);
                    }
                }),
        ),
});
